import { randomBytes } from 'crypto';
import { closeSync, existsSync, openSync, readFileSync, statSync, writeSync } from 'fs';
import { assertServerKeyPair, generateServerKeyPair, isCurveKey, ServerKeyPair } from './keys.js';
import { generateClusterSecret, mintJoinToken, MintJoinTokenInput } from './joinToken.js';

/**
 * The cluster key file `gotti keys init` writes and `servers.trust.keysFile`
 * points at. It is a SECRET (it holds secret keys and the cluster secret):
 * never commit it, and a host may carry a trimmed copy — an entry without
 * `secretKey` is a peer this host only needs to recognise, not run.
 */
export type ClusterKeysFile = {
  version: 1;
  clusterId: string;
  /** Root of join tokens. Needed where tokens are minted or verified (the master's host). */
  clusterSecret?: string;
  servers: { [serverId: string]: { publicKey: string; secretKey?: string } };
};

export function newClusterId(): string {
  return `c-${randomBytes(8).toString('hex')}`;
}

/** A complete key file for `serverIds` (fresh keypairs + cluster secret). */
export function createClusterKeys(serverIds: Array<string | number>, opts: { clusterId?: string } = {}): ClusterKeysFile {
  const servers: ClusterKeysFile['servers'] = {};
  for (const id of serverIds) {
    const key = String(id);
    if (servers[key]) throw new Error(`Duplicate server id '${key}' — every server in a cluster needs a distinct id.`);
    servers[key] = generateServerKeyPair();
  }
  return { version: 1, clusterId: opts.clusterId ?? newClusterId(), clusterSecret: generateClusterSecret(), servers };
}

/** Write a key file readable by its owner only. Refuses to overwrite unless `force`. */
export function writeClusterKeysFile(path: string, keys: ClusterKeysFile, opts: { force?: boolean } = {}): void {
  // 'wx' = O_CREAT|O_EXCL: no clobbering and no following a pre-placed symlink.
  const flags = opts.force ? 'w' : 'wx';
  if (!opts.force && existsSync(path)) {
    throw new Error(`Refusing to overwrite existing key file ${path} (pass --force to replace it — every server then needs the new file).`);
  }
  const fd = openSync(path, flags, 0o600);
  try {
    writeSync(fd, JSON.stringify(keys, null, 2) + '\n');
  } finally {
    closeSync(fd);
  }
}

/**
 * Read and validate a key file. Loud on everything: a missing file, a file
 * readable by group/other (it holds secrets — same rule ssh applies to keys),
 * a malformed entry, or a secret key that does not belong to its public key.
 */
export function loadClusterKeysFile(path: string): ClusterKeysFile {
  if (!existsSync(path)) {
    throw new Error(`servers.trust.keysFile '${path}' does not exist. Create one with \`gotti keys init\`.`);
  }
  if (process.platform !== 'win32') {
    const mode = statSync(path).mode & 0o777;
    if (mode & 0o077) {
      throw new Error(`servers.trust.keysFile '${path}' is readable by group/other (mode ${mode.toString(8)}); it holds secret keys — chmod 600 it.`);
    }
  }
  let parsed: any;
  try {
    parsed = JSON.parse(readFileSync(path, 'utf8'));
  } catch (err) {
    throw new Error(`servers.trust.keysFile '${path}' is not valid JSON: ${err instanceof Error ? err.message : String(err)}`);
  }
  return validateClusterKeys(parsed, path);
}

export function validateClusterKeys(parsed: any, label = 'cluster keys'): ClusterKeysFile {
  if (parsed?.version !== 1) throw new Error(`${label}: unsupported version ${parsed?.version} (expected 1).`);
  if (typeof parsed.clusterId !== 'string' || !parsed.clusterId) throw new Error(`${label}: clusterId is missing.`);
  if (parsed.clusterSecret !== undefined && typeof parsed.clusterSecret !== 'string') throw new Error(`${label}: clusterSecret must be a string.`);
  if (!parsed.servers || typeof parsed.servers !== 'object') throw new Error(`${label}: servers is missing.`);
  for (const [id, entry] of Object.entries<any>(parsed.servers)) {
    if (entry?.secretKey !== undefined) {
      assertServerKeyPair(id, entry);
    } else if (!isCurveKey(entry?.publicKey)) {
      throw new Error(`${label}: server '${id}' has no valid publicKey.`);
    }
  }
  return parsed as ClusterKeysFile;
}

/**
 * Where a Starter's cluster identities come from — exactly one of:
 *
 *  - `keysFile`: every server id must be in the file. A server this host runs
 *    needs its `secretKey` there; a peer needs at least its `publicKey`.
 *  - `generated` (no keys file configured): fresh in-memory keypairs for the
 *    servers THIS process hosts, a random cluster id and cluster secret. Peers
 *    outside the process cannot be known this way, so asking for one is an
 *    error that names the server and the fix.
 *
 * Dynamic members admitted at runtime (instances, host agents) are recorded
 * with `addMember` by whoever learned their key from an authenticated source.
 */
export class ClusterTrust {
  private readonly identities = new Map<string, { publicKey: string; secretKey?: string }>();

  private constructor(
    readonly source: 'keysFile' | 'generated',
    readonly clusterId: string,
    readonly clusterSecret: string | undefined,
    readonly keysFilePath?: string,
  ) {}

  static fromKeysFile(path: string): ClusterTrust {
    const keys = loadClusterKeysFile(path);
    const trust = new ClusterTrust('keysFile', keys.clusterId, keys.clusterSecret, path);
    for (const [id, entry] of Object.entries(keys.servers)) trust.identities.set(id, { ...entry });
    return trust;
  }

  static fromKeys(keys: ClusterKeysFile): ClusterTrust {
    const valid = validateClusterKeys(keys);
    const trust = new ClusterTrust('keysFile', valid.clusterId, valid.clusterSecret);
    for (const [id, entry] of Object.entries(valid.servers)) trust.identities.set(id, { ...entry });
    return trust;
  }

  static generated(opts: { clusterId?: string } = {}): ClusterTrust {
    return new ClusterTrust('generated', opts.clusterId ?? newClusterId(), generateClusterSecret());
  }

  /** Full identity for a server THIS process runs. */
  identityFor(serverId: string | number): ServerKeyPair {
    const id = String(serverId);
    const known = this.identities.get(id);
    if (known?.secretKey) return { publicKey: known.publicKey, secretKey: known.secretKey };
    if (this.source === 'generated' && !known) {
      const pair = generateServerKeyPair();
      this.identities.set(id, pair);
      return pair;
    }
    if (known) {
      throw new Error(`Cluster trust: server '${id}' is in ${this.keysFilePath ?? 'the keys file'} without its secretKey, so it cannot run on this host.`);
    }
    throw new Error(`Cluster trust: server '${id}' has no entry in ${this.keysFilePath ?? 'the keys file'} — regenerate it with \`gotti keys init\` (or add the server) so every host knows its key.`);
  }

  /** Public key of any server this process must talk to. */
  publicKeyFor(serverId: string | number): string {
    const id = String(serverId);
    const known = this.identities.get(id);
    if (known) return known.publicKey;
    if (this.source === 'generated') {
      throw new Error(
        `Cluster trust: the public key of server '${id}' is unknown. It is not hosted by this process, and no ` +
        `servers.trust.keysFile is configured — a multi-process or multi-host cluster needs one (\`gotti keys init\`).`,
      );
    }
    throw new Error(`Cluster trust: server '${id}' has no entry in ${this.keysFilePath ?? 'the keys file'}; peers with unknown keys are refused.`);
  }

  hasPublicKey(serverId: string | number): boolean {
    return this.identities.has(String(serverId));
  }

  /** Record a dynamic member whose key arrived from an authenticated source. A conflicting key is refused. */
  addMember(serverId: string | number, publicKey: string): void {
    if (!isCurveKey(publicKey)) throw new Error(`Cluster trust: member '${serverId}' has no valid publicKey.`);
    const known = this.identities.get(String(serverId));
    if (known && known.publicKey !== publicKey) {
      throw new Error(`Cluster trust: server '${serverId}' is already known with a different public key — refusing the impostor.`);
    }
    if (!known) this.identities.set(String(serverId), { publicKey });
  }

  /** Mint a join token for this cluster. Needs the cluster secret. */
  mintJoinToken(input: Omit<MintJoinTokenInput, 'clusterId'>): string {
    if (!this.clusterSecret) {
      throw new Error(`Cluster trust: no clusterSecret in ${this.keysFilePath ?? 'the keys file'}, so join tokens cannot be minted on this host.`);
    }
    return mintJoinToken(this.clusterSecret, { ...input, clusterId: this.clusterId });
  }
}

